import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { createAuthGuard } from 'keycloak-angular';
import { firstValueFrom } from 'rxjs';
import { GetPlatformRolesOperationsAction } from '../store/operation/operation.actions';
import { OperationState } from '../store/operation/operation.state';

/** Builds a hash-router redirect URI Keycloak can return to after login. */
function redirectUriFor(url: string): string {
  return `${window.location.origin}/#${url}`;
}

/**
 * Guards the authenticated route tree. When the user has no Keycloak session we
 * hand off to the Keycloak login page, asking it to return to the originally
 * requested URL once authentication completes.
 */
export const authGuard: CanActivateFn = createAuthGuard<CanActivateFn>(async (_route, state, authData) => {
  const { authenticated, keycloak } = authData;

  if (!authenticated) {
    await keycloak.login({ redirectUri: redirectUriFor(state.url) });
    return false;
  }

  return true;
});

/**
 * Per-route authorization guard: on top of the Keycloak session check it
 * verifies that the leaf route's `data.operation` is granted to the user.
 */
export const authChildGuard: CanActivateChildFn = createAuthGuard<CanActivateChildFn>(
  async (childRoute, state, authData) => {
    const router = inject(Router);
    const store = inject(Store);

    const { authenticated, keycloak } = authData;

    if (!authenticated) {
      await keycloak.login({ redirectUri: redirectUriFor(state.url) });
      return false;
    }

    // Only the leaf route carries the operation to check; intermediate
    // segments are traversed to reach an allowed page the user may not have
    // permission for at every step.
    if (childRoute.children.length > 0) {
      return true;
    }

    const operation = childRoute.data['operation'] as string | undefined;

    if (!operation) {
      return false;
    }

    // Guards run before route resolvers, so on the first navigation after login
    // the operation store is still empty (only UserResolver fills it, and that
    // runs after this guard). Under the legacy IdentityServer4 flow the
    // operations were loaded eagerly in auth.state on login; that path was
    // removed with the Keycloak migration. Trigger the cache-first load here and
    // wait for it so the very first authorization check sees the real data.
    await firstValueFrom(store.dispatch(new GetPlatformRolesOperationsAction()));

    const operations = await firstValueFrom(store.select(OperationState.getPlatformRolesOperations));

    if (operations?.includes(operation)) {
      return true;
    }

    router.navigate(['/error/permission-denied'], {
      state: {
        error: {
          message: `"${operation}" operation is not allowed for this child route`,
        },
      },
    });
    return false;
  },
);
